import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { useDepartments } from '../../../hooks/useDepartments';
import { departmentSubmissionService } from '../../../services/departmentSubmissionService';
import LoadingSpinner from '../../common/LoadingSpinner';
import { EmptyState } from '../../common/EmptyState';
import UniversalFilters from '../../common/UniversalFilters';
import Modal from '../../ui/Modal';
import Button from '../../ui/Button';
import Select from '../../ui/Select';
import { getFilterConfig } from '../../../constants/filterConfigs';
import { useFilters } from '../../../hooks/useFilters';
import { financialYearUtils, dateUtils } from '../../../utils/helpers';
import { USER_ROLES } from '../../../constants/authConstants';

const DepartmentSubmissions = () => {
  const { user, getUserDisplayName, getUserRole } = useAuth();
  const { isMobile, getResponsiveValue } = useTheme();
  const { departments, loading: loadingDepartments, getDepartmentLabel, getDepartmentOptions } = useDepartments();
  
  const isAdmin = getUserRole() === USER_ROLES.ADMIN;
  
  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  
  const {
    filters,
    handleFilterChange,
    clearFilters,
    hasActiveFilters
  } = useFilters({
    search: '',
    financialYear: '',
    department: ''
  });

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadDepartment, setUploadDepartment] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  
  const [actionLoading, setActionLoading] = useState('');
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const filterConfig = getFilterConfig('DEPARTMENT_SUBMISSIONS', {
    showDepartmentFilter: true
  });

  const titleSize = getResponsiveValue({
    mobile: 'text-xl',
    desktop: 'text-2xl'
  });

  const cardPadding = getResponsiveValue({
    mobile: 'p-4',
    desktop: 'p-6'
  });

  useEffect(() => {
    if (departments.length > 0) {
      loadSubmissions();
    }
  }, [filters.financialYear, departments]);

  useEffect(() => {
    applyFilters();
  }, [submissions, filters.search, filters.department]);

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      const result = await departmentSubmissionService.getAllSubmissions(filters.financialYear);
      
      if (result.success) {
        setSubmissions(result.data);
      }
    } catch (error) {
      console.error('Error loading submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...submissions];

    if (filters.search && filters.search.trim()) {
      const searchLower = filters.search.toLowerCase().trim();
      filtered = filtered.filter(submission => {
        const deptLabel = getDepartmentLabel(submission.department).toLowerCase();
        const fileName = submission.fileName.toLowerCase();
        const uploadedBy = submission.uploadedByName.toLowerCase();
        return deptLabel.includes(searchLower) || 
               fileName.includes(searchLower) || 
               uploadedBy.includes(searchLower);
      });
    }

    if (filters.department) {
      filtered = filtered.filter(submission => submission.department === filters.department);
    }

    setFilteredSubmissions(filtered);
  };

  const handleDownload = (fileUrl, fileName) => {
    window.open(fileUrl, '_blank');
  };

  const handleUnlock = async (submissionId, departmentName) => {
    if (!confirm(`Are you sure you want to unlock the submission for ${departmentName}?\n\nThis will allow the department to upload a new file.`)) {
      return;
    }

    try {
      setActionLoading(submissionId);
      const result = await departmentSubmissionService.unlockSubmission(
        submissionId, 
        user.uid, 
        getUserDisplayName()
      );
      
      if (result.success) {
        setSubmissions(prev => prev.map(sub => 
          sub.id === submissionId 
            ? { ...sub, isLocked: false, status: 'draft', lockedAt: null, lockedBy: null }
            : sub
        ));
        
        setSuccessMessage(result.message);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      }
    } catch (error) {
      alert('Failed to unlock submission: ' + error.message);
    } finally {
      setActionLoading('');
    }
  };

  const handleDelete = async (submissionId, departmentName) => {
    if (!confirm(`Are you sure you want to delete the submission for ${departmentName}?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      setActionLoading(submissionId);
      const result = await departmentSubmissionService.deleteSubmission(submissionId);
      
      if (result.success) {
        setSubmissions(prev => prev.filter(sub => sub.id !== submissionId));
        
        setSuccessMessage(result.message);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      }
    } catch (error) {
      alert('Failed to delete submission: ' + error.message);
    } finally {
      setActionLoading('');
    }
  };

  const handleAdminUpload = () => {
    setShowUploadModal(true);
    setUploadDepartment('');
    setUploadFile(null);
    setUploadError('');
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    setUploadError('');
    
    if (!file) return;
    
    if (file.type !== 'application/pdf') {
      setUploadError('Only PDF files are allowed');
      return;
    }
    
    const maxSize = 10 * 1024 * 1024; // 10 MB
    if (file.size > maxSize) {
      setUploadError('File size must be less than 10 MB');
      return;
    }
    
    setUploadFile(file);
  };

  const handleUploadSubmit = async () => {
    if (!uploadDepartment) {
      setUploadError('Please select a department');
      return;
    }
    
    if (!uploadFile) {
      setUploadError('Please select a PDF file');
      return;
    }

    try {
      setUploading(true);
      setUploadError('');
      
      const result = await departmentSubmissionService.adminUploadForDepartment(
        uploadFile,
        uploadDepartment,
        filters.financialYear,
        user.uid,
        getUserDisplayName()
      );
      
      if (result.success) {
        setSuccessMessage(result.message);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
        
        setShowUploadModal(false);
        loadSubmissions();
      }
    } catch (error) {
      setUploadError(error.message);
    } finally {
      setUploading(false);
    }
  };

  if (loading || loadingDepartments) {
    return (
      <div className="min-h-screen bg-bg-secondary flex items-center justify-center">
        <LoadingSpinner size="xl" text="Loading submissions..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {success && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-3 shadow-sm">
          <div className="flex items-center">
            <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="text-green-700 font-medium text-sm">{successMessage}</span>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className={`${titleSize} font-bold text-text-primary mb-1`}>Department Submissions</h1>
          <p className="text-text-secondary text-sm">
            View and manage PDF submissions from all departments
          </p>
        </div>

        <div className="flex items-center space-x-3">
          {isAdmin && (
            <Button
              onClick={handleAdminUpload}
              size="md"
              className="flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <span>Upload for Department</span>
            </Button>
          )}
          
          <UniversalFilters
            config={filterConfig}
            values={filters}
            onChange={handleFilterChange}
            onClearFilters={clearFilters}
            mode="dropdown"
          />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {filteredSubmissions.length === 0 ? (
          <div className={cardPadding}>
            <EmptyState
              icon={
                <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              }
              title={hasActiveFilters ? "No submissions match your filters" : "No submissions yet"}
              description={
                hasActiveFilters 
                  ? "Try adjusting your search or filter criteria"
                  : "Department submissions will appear here once they upload their documents"
              }
              actionLabel={hasActiveFilters ? "Clear Filters" : null}
              onAction={hasActiveFilters ? clearFilters : null}
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-16">
                    S.No.
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-40">
                    Department
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-24">
                    Financial Year
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-48">
                    File Name
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-20">
                    File Size
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-32">
                    Uploaded By
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 w-24">
                    Uploaded
                  </th>
                  <th className={`px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider ${isAdmin ? 'border-r border-gray-200' : ''} w-20`}>
                    Status
                  </th>
                  {isAdmin && (
                    <th className="px-3 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider w-32">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {filteredSubmissions.map((submission, index) => (
                  <tr key={submission.id} className="hover:bg-blue-50 transition-colors duration-200">
                    <td className="px-3 py-3 text-center border-r border-gray-200">
                      <div className="flex items-center justify-center w-6 h-6 bg-gray-100 rounded-full text-xs font-bold">
                        {index + 1}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-900 font-semibold border-r border-gray-200">
                      {getDepartmentLabel(submission.department)}
                    </td>
                    <td className="px-3 py-3 text-center text-xs text-gray-600 border-r border-gray-200">
                      FY {submission.financialYear}
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-900 border-r border-gray-200">
                      <div className="flex items-center space-x-2">
                        <svg className="w-4 h-4 text-red-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                        </svg>
                        <span className="truncate">{submission.fileName}</span>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-center text-xs text-gray-600 border-r border-gray-200">
                      {departmentSubmissionService.formatFileSize(submission.fileSize)}
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-600 border-r border-gray-200">
                      {submission.uploadedByName}
                    </td>
                    <td className="px-3 py-3 text-center text-xs text-gray-600 border-r border-gray-200">
                      {dateUtils.formatDate(submission.uploadedAt)}
                    </td>
                    <td className={`px-3 py-3 text-center ${isAdmin ? 'border-r border-gray-200' : ''}`}>
                      {submission.isLocked ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                          </svg>
                          Locked
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M10 2a5 5 0 00-5 5v2a2 2 0 00-2 2v5a2 2 0 002 2h10a2 2 0 002-2v-5a2 2 0 00-2-2H7V7a3 3 0 015.905-.75 1 1 0 001.937-.5A5.002 5.002 0 0010 2z" />
                          </svg>
                          Draft
                        </span>
                      )}
                    </td>
                    {isAdmin && (
                      <td className="px-3 py-3 text-center">
                        <div className="flex items-center justify-center space-x-2">
                          {/* Download */}
                          <button
                            onClick={() => handleDownload(submission.fileUrl, submission.fileName)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Download PDF"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </button>

                          {/* Unlock (if locked) */}
                          {submission.isLocked && (
                            <button
                              onClick={() => handleUnlock(submission.id, getDepartmentLabel(submission.department))}
                              disabled={actionLoading === submission.id}
                              className="p-1.5 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors disabled:opacity-50"
                              title="Unlock submission"
                            >
                              {actionLoading === submission.id ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-600"></div>
                              ) : (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                                </svg>
                              )}
                            </button>
                          )}

                          {/* Delete */}
                          <button
                            onClick={() => handleDelete(submission.id, getDepartmentLabel(submission.department))}
                            disabled={actionLoading === submission.id}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                            title="Delete submission"
                          >
                            {actionLoading === submission.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                            ) : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Admin Upload Modal */}
      <Modal
        isOpen={showUploadModal}
        onClose={() => !uploading && setShowUploadModal(false)}
        title="Upload Document for Department"
        size="md"
      >
        <div className="p-6 space-y-6">
          <p className="text-sm text-text-secondary">
            Upload a PDF document on behalf of a department for {filters.financialYear}
          </p>

          {uploadError && (
            <div className="bg-error-subtle border border-border-error rounded-xl p-3">
              <div className="flex items-center">
                <svg className="w-4 h-4 text-error mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span className="text-error text-sm">{uploadError}</span>
              </div>
            </div>
          )}

          <Select
            label="Department"
            value={uploadDepartment}
            onChange={(e) => setUploadDepartment(e.target.value)}
            options={getDepartmentOptions()}
            required
            placeholder="Select department"
            disabled={uploading}
          />

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              PDF Document <span className="text-error">*</span>
            </label>
            <div className="flex items-center justify-center w-full">
              <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-border-primary border-dashed rounded-xl cursor-pointer bg-surface-secondary hover:bg-surface-hover transition-colors ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  {uploadFile ? (
                    <>
                      <svg className="w-10 h-10 mb-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                      </svg>
                      <p className="mb-2 text-sm text-text-primary font-medium">{uploadFile.name}</p>
                      <p className="text-xs text-text-tertiary">{departmentSubmissionService.formatFileSize(uploadFile.size)}</p>
                    </>
                  ) : (
                    <>
                      <svg className="w-10 h-10 mb-3 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <p className="mb-2 text-sm text-text-secondary">
                        <span className="font-semibold">Click to upload</span> or drag and drop
                      </p>
                      <p className="text-xs text-text-tertiary">PDF only (Max 10 MB)</p>
                    </>
                  )}
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept="application/pdf"
                  onChange={handleFileSelect}
                  disabled={uploading}
                />
              </label>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-border-primary">
            <Button
              variant="outline"
              onClick={() => setShowUploadModal(false)}
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUploadSubmit}
              loading={uploading}
              disabled={uploading || !uploadDepartment || !uploadFile}
            >
              {uploading ? 'Uploading...' : 'Upload Document'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default DepartmentSubmissions;